/**
 * Did the trade make money? (website roadmap S27 — weav3r's premium "profit" view, like the "Arbitrage" script): every
 * buy and sale you made — Item Market and bazaar lines from your own log (trading-report.ts), and trades whose receipt you
 * copied with the extension's trade helper (kept with your lists: lists sync) — matched first in, first out per item, so
 * each sale carries the cost of the units you bought earliest and still had. Profit per sale, per item and per day.
 * Pure and browser-safe; tested in packages/shared/test/trade-profit.test.ts.
 *
 * Unknowns (never guessed):
 *  - A buy whose price isn't known still takes its place in the queue: the units sold from it have no cost, so their
 *    profit isn't counted ("uncosted") — skipping it would cost those sales at a later, wrong buy.
 *  - A sale whose price isn't known still uses up the units it took; no profit is counted for it.
 *  - A sale with nothing bought before it (items found, crafted, gifted, or bought before the kept log starts) has no
 *    cost: uncosted. FIFO never reaches forward to a later buy.
 *  - Items you used or gave away are not in the queue's view: their buys stay "still held" and later sales of the same
 *    item take them first (the page says so).
 */
import type { TradeLine } from './trading-report';

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);
const posInt = (x: unknown): number | null => {
  const n = num(x);
  return n !== null && Number.isInteger(n) && n > 0 ? n : null;
};
const str = (x: unknown, max = 80): string | null => (typeof x === 'string' && x.trim() ? x.trim().slice(0, max) : null);

// ---------------------------------------------------------------- receipts (the extension's trade helper, lists sync)

/** The lists-sync key the trade helper keeps your receipts under (extension core/storage-keys.ts TRADE_RECEIPTS). */
export const TRADE_RECEIPTS_LIST = 'omnitorn.trade.receipts';
/** The most receipts kept (newest first) — about 300 bytes each, well inside one synced list. */
export const TRADE_RECEIPTS_MAX = 300;

export interface ReceiptItem {
  name: string;
  /** Torn's item id, when the trade helper knew it. */
  id: number | null;
  qty: number;
  /** Price of one as the trade helper valued it (Torn's market value or the live price) — before the receipt's %. */
  each: number | null;
}

export interface ReceiptSide {
  money: number;
  items: ReceiptItem[];
}

/** One trade, as the trade helper saw it when you copied its receipt. */
export interface TradeReceipt {
  /** Torn's trade id (from the address), else `t<time>`. One receipt per trade: copying again replaces it. */
  id: string;
  /** When it was copied (ms). */
  at: number;
  partner: { id: number | null; name: string | null } | null;
  /** The receipt's share of the prices (100 = full price). */
  pct: number;
  /** What you put in. */
  gave: ReceiptSide;
  /** What the other player put in. */
  got: ReceiptSide;
}

function cleanItem(x: unknown): ReceiptItem | null {
  const o = obj(x);
  const name = str(o?.name);
  const qty = posInt(o?.qty);
  if (!o || !name || !qty) return null;
  const each = num(o.each);
  return { name, id: posInt(o.id), qty, each: each !== null && each >= 0 ? each : null };
}

function cleanSide(x: unknown): ReceiptSide {
  const o = obj(x);
  const money = num(o?.money);
  const items = Array.isArray(o?.items) ? (o!.items as unknown[]).map(cleanItem).filter((i): i is ReceiptItem => !!i).slice(0, 60) : [];
  return { money: money !== null && money > 0 ? Math.round(money) : 0, items };
}

/** One stored receipt, checked (null: not a receipt). */
export function cleanReceipt(x: unknown): TradeReceipt | null {
  const o = obj(x);
  const id = str(o?.id, 40);
  const at = num(o?.at);
  if (!o || !id || at === null || at <= 0) return null;
  const p = obj(o.partner);
  const partner = p ? { id: posInt(p.id), name: str(p.name, 40) } : null;
  const pct = num(o.pct);
  return { id, at, partner: partner && (partner.id || partner.name) ? partner : null, pct: pct !== null && pct > 0 && pct <= 1000 ? pct : 100, gave: cleanSide(o.gave), got: cleanSide(o.got) };
}

/** A stored list of receipts → the checked ones, newest first, one per trade id, at most TRADE_RECEIPTS_MAX. */
export function cleanReceipts(x: unknown): TradeReceipt[] {
  const seen = new Set<string>();
  return (Array.isArray(x) ? x : [])
    .map(cleanReceipt)
    .filter((r): r is TradeReceipt => !!r)
    .sort((a, b) => b.at - a.at)
    .filter((r) => !seen.has(r.id) && !!seen.add(r.id))
    .slice(0, TRADE_RECEIPTS_MAX);
}

/** Your receipts from the lists document (GET /api/me/lists `lists`). */
export function receiptsOf(lists: unknown): TradeReceipt[] {
  return cleanReceipts(obj(lists)?.[TRADE_RECEIPTS_LIST]);
}

/** Add (or replace, same trade id) a receipt in a stored list. */
export function withReceipt(list: unknown, r: TradeReceipt): TradeReceipt[] {
  return cleanReceipts([r, ...cleanReceipts(list).filter((x) => x.id !== r.id)]);
}

/** The lists document without one receipt (the website's "Remove": a trade that never went through). */
export function withoutReceipt(lists: unknown, id: string): Record<string, unknown> {
  const doc = { ...(obj(lists) ?? {}) };
  doc[TRADE_RECEIPTS_LIST] = receiptsOf(lists).filter((r) => r.id !== id);
  return doc;
}

/** Two devices' receipts joined (lists sync's first sync): every trade once, the newer copy of each. */
export function joinReceipts(here: unknown, there: unknown): TradeReceipt[] {
  return cleanReceipts([...cleanReceipts(here), ...cleanReceipts(there)]);
}

// ---------------------------------------------------------------- one line per item bought or sold

export type ProfitSource = 'market' | 'bazaar' | 'trade';
export const SOURCE_NAME: Record<ProfitSource, string> = { market: 'Item Market', bazaar: 'Bazaar', trade: 'Trade' };

/** Units of one item bought or sold, with what they cost or brought in. */
export interface ProfitLine {
  id: string;
  at: number;
  source: ProfitSource;
  side: 'buy' | 'sell';
  itemId: number;
  qty: number;
  /** A buy: what it cost. A sale: what came in after the Item Market's fee. Null: unknown. */
  amount: number | null;
  /** The other player's id (null: anonymous or not said). */
  party: number | null;
}

/** A log line (trading-report.ts) as a profit line. */
export function fromTradeLine(l: TradeLine): ProfitLine {
  return { id: l.id, at: l.at, source: l.venue, side: l.side, itemId: l.itemId, qty: l.qty, amount: l.total === null ? null : l.side === 'sell' ? l.total - l.fee : l.total, party: l.party };
}

/**
 * Prices for one side's items from the money that went the other way. Every item priced: the money split by those
 * prices. Exactly one unpriced: it takes what's left after the priced ones (if anything is). Otherwise the unpriced
 * stay unknown and the priced keep their own price.
 */
function splitMoney(items: ReadonlyArray<{ qty: number; each: number | null }>, money: number): Array<number | null> {
  if (items.length === 1) return [money];
  const priced = items.reduce((s, i) => s + (i.each === null ? 0 : i.each * i.qty), 0);
  const unpriced = items.filter((i) => i.each === null).length;
  if (!unpriced) return items.map((i) => (priced > 0 ? (i.each! * i.qty * money) / priced : null));
  if (unpriced === 1 && money - priced >= 0) return items.map((i) => (i.each === null ? money - priced : i.each * i.qty));
  return items.map((i) => (i.each === null ? null : i.each * i.qty));
}

/**
 * A receipt → its profit lines: what you got are buys, what you gave are sales. The money that went the other way pays
 * for them when only one side has items (split by the receipt's prices — the receipt's % of them for what you got).
 * Items both ways (a swap), or no money at all: each item at the receipt's price (unknown when it had none). Items
 * whose id can't be found are left out (`idOf` maps a name to Torn's id).
 */
export function receiptLines(r: TradeReceipt, idOf: (name: string) => number | null = () => null): ProfitLine[] {
  const rate = r.pct / 100;
  const net = r.gave.money - r.got.money; // > 0: you paid; < 0: you were paid.
  const side = (items: ReceiptItem[], buy: boolean): ProfitLine[] => {
    const priced = items.map((i) => ({ ...i, each: i.each === null ? null : buy ? i.each * rate : i.each }));
    const paysFor = buy ? net > 0 && !r.gave.items.length : net < 0 && !r.got.items.length;
    const amounts = paysFor ? splitMoney(priced, Math.abs(net)) : priced.map((i) => (i.each === null ? null : i.each * i.qty));
    return priced.flatMap((i, n): ProfitLine[] => {
      const itemId = i.id ?? idOf(i.name);
      if (!itemId) return [];
      const a = amounts[n];
      return [{ id: `trade:${r.id}:${buy ? 'b' : 's'}${n}`, at: r.at, source: 'trade', side: buy ? 'buy' : 'sell', itemId, qty: i.qty, amount: a === null || a === undefined ? null : Math.round(a), party: r.partner?.id ?? null }];
    });
  };
  return [...side(r.got.items, true), ...side(r.gave.items, false)];
}

// ---------------------------------------------------------------- first in, first out

/** Units of one buy a sale took. */
export interface LotTaken {
  buyId: string;
  at: number;
  source: ProfitSource;
  qty: number;
  /** Cost of one (null: that buy's price isn't known). */
  each: number | null;
}

export interface MatchedSale {
  line: ProfitLine;
  /** The buys it took units from, oldest first. */
  lots: LotTaken[];
  /** Units with a known cost and a known sale price — the ones whose profit counts. */
  costed: number;
  /** Units whose profit can't be worked out: an unpriced buy or sale. */
  uncosted: number;
  /** …of them, units with no buy before them at all. */
  noBuy: number;
  /** Cost of the costed units. */
  cost: number;
  /** What the costed units brought in (the sale's share). */
  proceeds: number;
  /** proceeds − cost (null: no unit's profit can be worked out). */
  profit: number | null;
}

/** Units still held from a buy (not sold yet, as far as the trades show). */
export interface OpenLot {
  buyId: string;
  itemId: number;
  at: number;
  source: ProfitSource;
  qty: number;
  each: number | null;
}

export interface FifoResult {
  sales: MatchedSale[];
  open: OpenLot[];
}

/**
 * Match every sale to the earliest buys of the same item still held (first in, first out). Run it over everything you
 * have — all of the kept log and every receipt — then look at the sales in the dates you want, so a sale in the range
 * finds the buys made before it.
 */
export function fifoMatch(lines: readonly ProfitLine[]): FifoResult {
  // Oldest first; at the same moment, buys before sales.
  const sorted = [...lines].sort((a, b) => a.at - b.at || (a.side === b.side ? 0 : a.side === 'buy' ? -1 : 1) || a.id.localeCompare(b.id));
  const queues = new Map<number, OpenLot[]>();
  const sales: MatchedSale[] = [];
  for (const l of sorted) {
    const q = queues.get(l.itemId) ?? [];
    queues.set(l.itemId, q);
    if (l.side === 'buy') {
      q.push({ buyId: l.id, itemId: l.itemId, at: l.at, source: l.source, qty: l.qty, each: l.amount === null ? null : l.amount / l.qty });
      continue;
    }
    const lots: LotTaken[] = [];
    let left = l.qty;
    while (left > 0 && q.length) {
      const lot = q[0]!;
      const take = Math.min(left, lot.qty);
      lots.push({ buyId: lot.buyId, at: lot.at, source: lot.source, qty: take, each: lot.each });
      lot.qty -= take;
      left -= take;
      if (!lot.qty) q.shift();
    }
    const saleEach = l.amount === null ? null : l.amount / l.qty;
    const costedLots = saleEach === null ? [] : lots.filter((x) => x.each !== null);
    const costed = costedLots.reduce((s, x) => s + x.qty, 0);
    const cost = costedLots.reduce((s, x) => s + x.qty * x.each!, 0);
    const proceeds = saleEach === null ? 0 : saleEach * costed;
    sales.push({ line: l, lots, costed, uncosted: l.qty - costed, noBuy: left, cost: Math.round(cost), proceeds: Math.round(proceeds), profit: costed ? Math.round(proceeds) - Math.round(cost) : null });
  }
  const open = [...queues.values()].flat().sort((a, b) => a.at - b.at);
  return { sales, open };
}

// ---------------------------------------------------------------- per item, per day, in all

export interface ItemProfit {
  itemId: number;
  /** Units sold (in the sales given). */
  sold: number;
  /** Units whose profit counts, what they brought in and cost. */
  costed: number;
  proceeds: number;
  cost: number;
  profit: number | null;
  /** Profit as a share of cost (%), null when nothing costed. */
  margin: number | null;
  uncosted: number;
  /** Units still held (all buys not yet sold) and what they cost (known prices only). */
  held: number;
  heldCost: number;
  /** Held units whose price isn't known. */
  heldUnknown: number;
  sales: number;
  last: number;
}

/** Per item: the sales given (e.g. those in the dates) and what's still held, by profit (best first). */
export function profitByItem(sales: readonly MatchedSale[], open: readonly OpenLot[] = []): ItemProfit[] {
  const by = new Map<number, ItemProfit>();
  const row = (itemId: number) => {
    let r = by.get(itemId);
    if (!r) by.set(itemId, (r = { itemId, sold: 0, costed: 0, proceeds: 0, cost: 0, profit: null, margin: null, uncosted: 0, held: 0, heldCost: 0, heldUnknown: 0, sales: 0, last: 0 }));
    return r;
  };
  for (const s of sales) {
    const r = row(s.line.itemId);
    r.sales++;
    r.sold += s.line.qty;
    r.costed += s.costed;
    r.proceeds += s.proceeds;
    r.cost += s.cost;
    r.uncosted += s.uncosted;
    r.last = Math.max(r.last, s.line.at);
    if (s.profit !== null) r.profit = (r.profit ?? 0) + s.profit;
  }
  for (const o of open) {
    const r = by.get(o.itemId);
    if (!r) continue; // only items in view
    r.held += o.qty;
    if (o.each === null) r.heldUnknown += o.qty;
    else r.heldCost += Math.round(o.each * o.qty);
  }
  for (const r of by.values()) r.margin = r.cost > 0 && r.profit !== null ? Math.round((r.profit / r.cost) * 1000) / 10 : null;
  return [...by.values()].sort((a, b) => (b.profit ?? -Infinity) - (a.profit ?? -Infinity) || b.sold - a.sold || a.itemId - b.itemId);
}

export interface ProfitTotals {
  sales: number;
  sold: number;
  costed: number;
  proceeds: number;
  cost: number;
  profit: number;
  margin: number | null;
  uncosted: number;
  noBuy: number;
  /** Sales that made money / lost money (counted ones). */
  wins: number;
  losses: number;
}

export function profitTotals(sales: readonly MatchedSale[]): ProfitTotals {
  const t: ProfitTotals = { sales: 0, sold: 0, costed: 0, proceeds: 0, cost: 0, profit: 0, margin: null, uncosted: 0, noBuy: 0, wins: 0, losses: 0 };
  for (const s of sales) {
    t.sales++;
    t.sold += s.line.qty;
    t.costed += s.costed;
    t.proceeds += s.proceeds;
    t.cost += s.cost;
    t.profit += s.profit ?? 0;
    t.uncosted += s.uncosted;
    t.noBuy += s.noBuy;
    if (s.profit !== null && s.profit > 0) t.wins++;
    else if (s.profit !== null && s.profit < 0) t.losses++;
  }
  t.margin = t.cost > 0 ? Math.round((t.profit / t.cost) * 1000) / 10 : null;
  return t;
}

/** Cost of one unit sold (FIFO, the costed units' average), null when none costed. */
export const costEach = (s: MatchedSale): number | null => (s.costed ? s.cost / s.costed : null);
/** What one unit sold for (after fees), null when unknown. */
export const saleEach = (s: MatchedSale): number | null => (s.line.amount === null ? null : s.line.amount / s.line.qty);

/** The matched sales as CSV rows (log-kit `toCsv`). */
export function profitCsvRows(sales: readonly MatchedSale[], name: (id: number) => string): { header: string[]; rows: Array<Array<string | number | null>> } {
  const r = (x: number | null) => (x === null ? null : Math.round(x * 100) / 100);
  return {
    header: ['time_utc', 'where', 'item_id', 'item', 'quantity', 'sale_each_after_fee', 'cost_each_fifo', 'units_costed', 'units_uncosted', 'units_without_buy', 'proceeds', 'cost', 'profit', 'bought_from'],
    rows: sales.map((s) => [new Date(s.line.at).toISOString(), SOURCE_NAME[s.line.source], s.line.itemId, name(s.line.itemId), s.line.qty, r(saleEach(s)), r(costEach(s)), s.costed, s.uncosted, s.noBuy, s.costed ? s.proceeds : null, s.costed ? s.cost : null, s.profit, s.lots.map((x) => `${x.qty}@${x.each === null ? '?' : Math.round(x.each)} ${SOURCE_NAME[x.source]} ${new Date(x.at).toISOString()}`).join('; ')]),
  };
}
